import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle2, Globe, Clock, TrendingUp, Award } from 'lucide-react';
import { services, stats, whyChooseUs } from '../altiora-mock';

const AltioraHome = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-32 md:py-40 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1920&q=80" 
            alt="Business Finance"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/95 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl space-y-8">
            <Badge className="bg-teal-600 hover:bg-teal-700 text-white border-none text-sm px-5 py-2.5 font-semibold">
              Trusted Business Solutions Partner
            </Badge>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight">
              End-to-End Finance &{' '}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-400 mt-2">
                Business Process Outsourcing
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl font-light leading-relaxed">
              Altiora Business Solutions empowers small and growing businesses with enterprise-grade <strong className="font-semibold text-white">Order-to-Cash, AR, AP, Claims Recovery, Shipping & Logistics support and Customer Services</strong> — delivered with speed, accuracy and measurable results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 pt-6">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white font-bold px-10 py-7 text-lg group transition-all shadow-xl hover:shadow-2xl">
                  Get Free Consultation
                  <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-10 py-7 text-lg transition-all">
                  View Services
                </Button>
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-8 pt-12 text-sm border-t border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-600/20 rounded-full flex items-center justify-center">
                  <Globe className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <div className="font-bold text-lg">24/7</div>
                  <div className="text-gray-400">Global Support</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-600/20 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <div className="font-bold text-lg">17+ Years</div>
                  <div className="text-gray-400">Experience</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-600/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <div className="font-bold text-lg">30 Days</div>
                  <div className="text-gray-400">Best Recovery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-teal-600 mb-2">{stat.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Our Core Services</h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive accounting and business process solutions tailored for your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Service 1: AR */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80" 
                  alt="Accounts Receivable"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Accounts Receivable (AR)</h3>
                <p className="text-gray-600">End-to-end AR management to optimize cash flow.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Service 2: AP */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80" 
                  alt="Accounts Payable"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Accounts Payable (AP)</h3>
                <p className="text-gray-600">Streamline AP processes with accuracy and efficiency.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Service 3: Claims Recovery */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" 
                  alt="Claims Recovery"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Claims Recovery</h3>
                <p className="text-gray-600">Maximize recoveries with proven strategies.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Service 4: Customer Services */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80" 
                  alt="Customer Services"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Customer Services</h3>
                <p className="text-gray-600">24/7 support for returns and subscriptions.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Service 5: Purchase Order Management */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80" 
                  alt="Purchase Order Management"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Purchase Order Management</h3>
                <p className="text-gray-600">Managing PO entries in ERP systems.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Service 6: Daily Invoicing */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&q=80" 
                  alt="Daily Invoicing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Daily Invoicing Process</h3>
                <p className="text-gray-600">Automated daily invoicing in ARP systems.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>

            {/* Service 7: Journal & Ledger */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-teal-500 bg-white overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1554224311-beee4f88c95f?w=800&q=80" 
                  alt="Journal Ledger"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">Journal & Ledger Entries</h3>
                <p className="text-gray-600">Accurate financial record-keeping and entries.</p>
                <Link to="/services" className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white font-bold px-10 py-6">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Image Side */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80" 
                    alt="Team Collaboration"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 to-transparent"></div>
                </div>
                {/* Floating Stats */}
                <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-2xl p-6 border-t-4 border-teal-600">
                  <div className="text-5xl font-black text-teal-600 mb-2">95%</div>
                  <div className="text-sm font-semibold text-gray-900">Client Satisfaction</div>
                  <div className="text-xs text-gray-600 mt-1">Consistently Rated</div>
                </div>
              </div>

              {/* Content Side */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                    Why Choose <span className="text-teal-600">Altiora?</span>
                  </h2>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    We deliver exceptional results with integrity and expertise, helping you focus on what matters most - growing your business.
                  </p>
                </div>
                
                <div className="space-y-6">
                  {whyChooseUs.map((item) => (
                    <div key={item.id} className="flex gap-5 group">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-600 transition-colors">
                          <CheckCircle2 className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" 
            alt="Office Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 to-blue-900/95"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto space-y-10">
            <h2 className="text-4xl md:text-6xl font-black text-white leading-tight">
              Ready to Transform Your Financial Operations?
            </h2>
            <p className="text-xl md:text-2xl text-gray-200 font-light">
              Get a free consultation with our accounting experts and discover how we can help your business grow.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-teal-900 hover:bg-gray-100 font-black px-12 py-8 text-xl shadow-2xl hover:shadow-3xl transition-all">
                Schedule Free Consultation
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
            </Link>
            <div className="flex items-center justify-center gap-8 pt-8 text-white/90 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span>No commitment required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span>24-hour response time</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AltioraHome;
